import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import { ChartOptions, ChartData, ChartDataset } from 'chart.js';

import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BarChartComponent implements OnChanges {
  constructor(private translate: TranslateService) {}
  /**
   * Title shown above the chart (inside the card).
   */
  @Input() title: string = '';

  /**
   * Labels for each bar (x-axis). E.g. ['item1', 'item2', ...]
   */
  @Input() labels: string[] = [];

  /**
   * Data values for each bar (y-axis). E.g. [17, 30, 24, ...]
   * labels.length should match data.length.
   */
  @Input() data: number[] = [];

  /**
   * Optional array of colors for bars. If length matches data.length,
   * each bar uses corresponding color; otherwise fallback palette used.
   */
  @Input() colors?: string[];

  /**
   * Optional: label for the dataset (shown in tooltip). E.g. “Profit”.
   * If not provided, Chart.js will show nothing or default.
   */
  @Input() datasetLabel: string = '';

  // Chart.js data object
  public chartData: ChartData<'bar', number[], string> = {
    labels: [],
    datasets: []
  };

  // Chart.js options
  public chartOptions: ChartOptions<'bar'> = {};

  ngOnChanges(changes: SimpleChanges): void {
    // Only rebuild when labels or data change, and lengths match
    if ((changes['labels'] || changes['data']) &&
      this.labels && this.data) {
      this.buildChart();
    }
  }

  private buildChart() {
    // Prepare colors for bars
    let bgColors: string[] = [];
    if (this.colors && this.colors.length >= this.data.length) {
      bgColors = this.colors.slice(0, this.data.length);
    } else {
      // Fallback palette; can extend or customize
      const defaultPalette = [
        '#FF6384',
        '#36A2EB',
        '#FFCE56',
        '#4BC0C0',
        '#9966FF',
        '#FF9F40',
        '#8A2BE2',
        '#00CED1',
        '#FF4500',
        '#2E8B57'
      ];
      for (let i = 0; i < this.data.length; i++) {
        bgColors.push(defaultPalette[i % defaultPalette.length]);
      }
    }

    const isAllZero = !this.data || this.data.length === 0 || this.data.every(v => !v);

    let chartDataValues = this.data;
    let finalBgColors = bgColors;

    if (isAllZero && this.labels && this.labels.length > 0) {
      chartDataValues = this.labels.map(() => 1); // placeholder values
      finalBgColors = this.labels.map(() => '#e9ecef'); // grey out
    } else if (isAllZero) {
      this.labels = ['No Data'];
      chartDataValues = [1];
      finalBgColors = ['#e9ecef'];
    }

    const datasetLabelTranslated = this.datasetLabel ? this.translate.instant(this.datasetLabel) : undefined;

    // Build single dataset
    const dataset: ChartDataset<'bar', number[]> = {
      data: chartDataValues,
      label: datasetLabelTranslated,
      backgroundColor: finalBgColors,
      // You can set borderColor or borderWidth if desired
      // borderColor: bgColors.map(c => darken(c)), borderWidth: 1
    };

    // Assign a fresh chartData object so ng2-charts picks it up
    this.chartData = {
      labels: this.labels,
      datasets: [dataset]
    };

    // Chart options
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          // x-axis options
          ticks: {
            autoSkip: false,
            maxRotation: 45,
            minRotation: 0,
            // If labels may be long, consider wrapping or rotating
          },
          grid: {
            display: false
          }
        },
        y: {
          beginAtZero: true,
          // Optionally set suggestedMax or stepSize
          // suggestedMax: Math.max(...this.data) * 1.1,
          grid: {
            // drawBorder: false
          },
          ticks: {
            stepSize: isAllZero ? 1 : undefined,
            callback: (value) => {
              if (isAllZero) return value === 1 ? '1' : '0';
              return `${value}`;
            }
          }
        }
      },
      plugins: {
        legend: {
          display: false // hide built-in legend, since single dataset
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              if (isAllZero) return '0';
              const label = context.dataset.label ? `${context.dataset.label}: ` : '';
              const value = context.parsed.y;
              return `${label}${value}`;
            }
          }
        }
      }
    };
  }
}
